import { useState, useEffect } from 'react';
import {
	Modal,
	Form,
	Input,
	InputNumber,
	Select,
	Switch,
	message,
	Tabs,
	Typography,
	Row,
	Col,
	Spin,
	Button,
	Breadcrumb,
	Tag,
} from 'antd';
import { CALL_SNAPPY_API } from './RewardsStoreGraphQL';
import { countryOptions, budgetRanges } from './options.js';
import { AnimatedRewardCard } from './AnimatedRewardCard.jsx';

const AddRewardModal = ({
	open,
	onCancel,
	onSubmit,
	availabilityGroups,
	tabSettings,
	currentUser,
	client,
	loading = false,
	rewardsStoreSettings,
}) => {
	const [form] = Form.useForm();
	const [activeTab, setActiveTab] = useState('snappy');
	const [selectedCountry, setSelectedCountry] = useState('US');
	const [selectedBudgetValue, setSelectedBudgetValue] = useState(100);
	const [selectedBudget, setSelectedBudget] = useState({
		minBudget: 76,
		maxBudget: 100,
	});
	const [collections, setCollections] = useState([]);
	const [collectionsLoading, setCollectionsLoading] = useState(false);
	const [productsByCollection, setProductsByCollection] = useState({});
	const [productsLoading, setProductsLoading] = useState(false);
	const [selectedCollection, setSelectedCollection] = useState(null);
	const [wizardStep, setWizardStep] = useState('collections'); // 'collections', 'products', or 'details'
	const [currentSearchTerm, setCurrentSearchTerm] = useState('');
	const [activeProduct, setActiveProduct] = useState(null);
	const [selectedVariant, setSelectedVariant] = useState(null);
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);

	const handleBudgetChange = (value) => {
		setSelectedBudgetValue(value);
		const budgetRange = budgetRanges.find((range) => range.value === value);
		if (budgetRange) {
			setSelectedBudget({
				minBudget: budgetRange.minBudget,
				maxBudget: budgetRange.maxBudget,
			});
		} else {
			setSelectedBudget(null);
		}
	};

	const checkOptionsObject = (obj = {}) => {
		const optionTypes = [
			'takeYourPick',
			'color',
			'size',
			'scent',
			'flavor',
			'beverageType',
		];
		let matchingIndex = null;
		optionTypes.forEach((optionType, index) => {
			if (obj?.[optionType]) {
				matchingIndex = index;
			}
		});
		if (obj?.[optionTypes[matchingIndex]]) {
			return obj[optionTypes[matchingIndex]];
		}
		return null;
	};

	const fetchSnappyProductData = async ({
		collectionId = null,
		searchTerm = null,
	}) => {
		if (!selectedCountry || !selectedBudget) {
			return;
		}
		const isProduct = collectionId || searchTerm;
		try {
			if (isProduct) {
				setProductsLoading(true);
			} else {
				setCollectionsLoading(true);
			}
			const queryParams = [
				{
					key: 'companyId',
					value: 'true',
				},
				{
					key: 'accountId',
					value: 'true',
				},
				{
					key: 'fields',
					value: 'full',
				},
				{
					key: isProduct ? 'country' : 'countries',
					value: selectedCountry,
				},
				{
					key: isProduct ? 'maxBudget' : 'budget',
					value: selectedBudget.maxBudget,
				},
			];
			if (isProduct) {
				if (searchTerm) {
					queryParams.push({ key: 'title', value: searchTerm });
				}
				if (collectionId) {
					queryParams.push({ key: 'collectionId', value: collectionId });
				}
				queryParams.push({ key: 'minBudget', value: selectedBudget.minBudget });
			}

			const variables = {
				companyId: currentUser.company.id,
				endpoint: isProduct ? 'products' : 'collections',
				queryParams,
				method: 'GET',
			};

			const { data } = await client.query({
				query: CALL_SNAPPY_API,
				variables,
				fetchPolicy: 'network-only',
			});

			if (data?.callSnappyApi) {
				try {
					const parsedResponse = JSON.parse(data.callSnappyApi);
					const results = parsedResponse?.results || [];
					if (results && Array.isArray(results)) {
						if (isProduct && collectionId && !searchTerm) {
							setProductsByCollection((prev) => ({
								...prev,
								[collectionId]: results,
							}));
						} else if (isProduct && searchTerm) {
							setProductsByCollection((prev) => ({
								...prev,
								manualSearch: results,
							}));
						} else if (!isProduct) {
							setCollections(results);
						}
					} else {
						if (!isProduct) {
							setCollections([]);
						}
					}
				} catch (parseError) {
					console.error('Error parsing Snappy API response:', parseError);
					if (!isProduct) {
						setCollections([]);
					}
				}
			} else {
				if (!isProduct) {
					setCollections([]);
				}
			}
		} catch (error) {
			console.error(
				`Error fetching ${isProduct ? 'products' : 'collections'}:`,
				error
			);
			message.error(
				`Failed to fetch ${isProduct ? 'products' : 'collections'}`
			);
			if (!isProduct) {
				setCollections([]);
			}
		} finally {
			if (isProduct) {
				setProductsLoading(false);
			} else {
				setCollectionsLoading(false);
			}
		}
	};

	const handleCollectionClick = (collection) => {
		setSelectedCollection(collection);
		setWizardStep('products');

		if (productsByCollection[collection.id]) {
			return;
		}

		fetchSnappyProductData({ collectionId: collection.id });
	};

	const handleBackToCollections = () => {
		setWizardStep('collections');
		setSelectedCollection(null);
		setCurrentSearchTerm('');
		setActiveProduct(null);
		setSelectedVariant(null);
		setSelectedImageIndex(0);
	};

	const handleBackToProducts = () => {
		setWizardStep('products');
		setActiveProduct(null);
		setSelectedVariant(null);
		setSelectedImageIndex(0);
	};

	const handleProductClick = (product) => {
		setActiveProduct(product);
		if (product.variants && product.variants.length > 0) {
			setSelectedVariant(product.variants[0]);
		}
		setSelectedImageIndex(0);
		setWizardStep('details');
	};

	const handleVariantChange = (variant) => {
		setSelectedVariant(variant);
		setSelectedImageIndex(0);
	};

	const handleSearch = () => {
		if (currentSearchTerm.trim()) {
			setSelectedCollection({ id: 'manualSearch' });
			setWizardStep('products');
			fetchSnappyProductData({ searchTerm: currentSearchTerm.trim() });
		}
	};

	useEffect(() => {
		if (selectedCountry && selectedBudget) {
			handleBackToCollections();
			setProductsByCollection({});
			fetchSnappyProductData({ collectionId: null, searchTerm: null });
		} else {
			setProductsByCollection({});
			setCollections([]);
		}
	}, [selectedCountry, selectedBudget]);

	const handleSubmit = async (values) => {
		try {
			await onSubmit(values);
			if (activeTab === 'manual') {
				form.resetFields();
			}
		} catch (error) {
			console.error('Error in AddRewardModal:', error);
		}
	};

	const handleCancel = () => {
		if (activeTab === 'manual') {
			form.resetFields();
		}
		setActiveTab('snappy');
		setSelectedCountry('US');
		setSelectedBudgetValue(100);
		setSelectedBudget({ minBudget: 76, maxBudget: 100 });
		setCollections([]);
		setProductsByCollection({});
		setSelectedCollection(null);
		setWizardStep('collections');
		setCurrentSearchTerm('');
		setActiveProduct(null);
		setSelectedVariant(null);
		setSelectedImageIndex(0);
		onCancel();
	};

	const getRewardTypeOptions = () => {
		const defaultNames = {
			gifts: 'Gifts',
			giftCards: 'Gift Cards',
			nonMonetary: 'Non-Monetary',
			swag: 'Swag',
		};

		return Object.keys(defaultNames).map((rewardType) => ({
			value: rewardType,
			label: tabSettings?.[rewardType]?.name || defaultNames[rewardType],
		}));
	};

	const renderManualForm = () => (
		<Form
			form={form}
			layout="vertical"
			onFinish={handleSubmit}
			initialValues={{
				active: true,
			}}
		>
			<Form.Item
				name="rewardType"
				label="Reward Type"
				rules={[{ required: true, message: 'Please select reward type' }]}
			>
				<Select
					placeholder="Select reward type"
					options={getRewardTypeOptions()}
				/>
			</Form.Item>

			<Form.Item
				name="name"
				label="Reward Name"
				rules={[{ required: true, message: 'Please enter reward name' }]}
			>
				<Input placeholder="Enter reward name" />
			</Form.Item>

			<Form.Item
				name="description"
				label="Description"
				rules={[{ required: true, message: 'Please enter description' }]}
			>
				<Input.TextArea rows={3} placeholder="Enter reward description" />
			</Form.Item>

			<Form.Item
				name="amount"
				label="Points Amount"
				rules={[{ required: true, message: 'Please enter points amount' }]}
			>
				<InputNumber
					min={0}
					placeholder="Enter points amount"
					style={{ width: '100%' }}
				/>
			</Form.Item>

			<Form.Item name="availabilityGroupIds" label="Availability Groups">
				<Select
					allowClear
					mode="multiple"
					placeholder="Select availability groups"
					options={availabilityGroups.map((ag) => ({
						label: ag.name,
						value: ag.id,
					}))}
				/>
			</Form.Item>

			<Form.Item name="image" label="Image URL">
				<Input placeholder="Enter image URL" />
			</Form.Item>

			<Form.Item name="active" label="Status" valuePropName="checked">
				<Switch checkedChildren="Active" unCheckedChildren="Inactive" />
			</Form.Item>
		</Form>
	);

	const renderBreadcrumb = () => {
		return (
			<Breadcrumb
				style={{ marginBottom: 8, marginTop: 8, fontSize: 16, fontWeight: 700 }}
				separator=">"
			>
				<Breadcrumb.Item>
					{wizardStep !== 'collections' ? (
						<span
							onClick={() => handleBackToCollections()}
							style={{ cursor: 'pointer', color: '#1890ff' }}
						>
							Collections
						</span>
					) : (
						'Collections'
					)}
				</Breadcrumb.Item>

				{(wizardStep === 'products' || wizardStep === 'details') && (
					<Breadcrumb.Item>
						{wizardStep === 'details' ? (
							<span
								onClick={() => handleBackToProducts()}
								style={{ cursor: 'pointer', color: '#1890ff' }}
							>
								{selectedCollection?.name ||
									`Results for: "${currentSearchTerm}"`}
							</span>
						) : (
							selectedCollection?.name || `Results for: "${currentSearchTerm}"`
						)}
					</Breadcrumb.Item>
				)}

				{wizardStep === 'details' && activeProduct && (
					<Breadcrumb.Item>
						{activeProduct.title || selectedVariant?.title || 'Product Details'}
					</Breadcrumb.Item>
				)}
			</Breadcrumb>
		);
	};

	const renderProductDetails = () => {
		if (!activeProduct || !selectedVariant) return null;

		const hasMultipleImages =
			selectedVariant.mediaItems && selectedVariant.mediaItems.length > 1;
		const hasMultipleVariants =
			activeProduct.variants && activeProduct.variants.length > 1;
		const hasOptions = checkOptionsObject(activeProduct?.options);

		return (
			<div>
				<Row gutter={24} style={{ height: '600px' }}>
					<Col span={12}>
						<div
							style={{
								height: '100%',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<div
								style={{
									flex: 1,
									marginBottom: hasMultipleImages ? 16 : 0,
									border: '1px solid #f0f0f0',
									borderRadius: '8px',
									overflow: 'hidden',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									backgroundColor: '#fafafa',
								}}
							>
								{selectedVariant.mediaItems &&
								selectedVariant.mediaItems[selectedImageIndex] ? (
									<img
										src={selectedVariant.mediaItems[selectedImageIndex].src}
										alt={selectedVariant.title}
										style={{
											maxWidth: '100%',
											maxHeight: '100%',
											objectFit: 'contain',
										}}
									/>
								) : (
									<div style={{ color: '#595959', textAlign: 'center' }}>
										No image available
									</div>
								)}
							</div>
							{hasMultipleImages && (
								<div
									style={{
										display: 'flex',
										gap: 8,
										overflowX: 'auto',
										paddingBottom: 8,
									}}
								>
									{selectedVariant.mediaItems.map((mediaItem, index) => (
										<div
											key={index}
											onClick={() => setSelectedImageIndex(index)}
											style={{
												width: 80,
												height: 80,
												border:
													selectedImageIndex === index
														? '2px solid #1890ff'
														: '1px solid #f0f0f0',
												borderRadius: '6px',
												overflow: 'hidden',
												cursor: 'pointer',
												flexShrink: 0,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												backgroundColor: '#fafafa',
											}}
										>
											<img
												src={mediaItem.src}
												alt={`${selectedVariant.title} ${index + 1}`}
												style={{
													width: '100%',
													height: '100%',
													objectFit: 'cover',
												}}
											/>
										</div>
									))}
								</div>
							)}
						</div>
					</Col>
					<Col span={12}>
						<div
							style={{
								height: '100%',
								display: 'flex',
								flexDirection: 'column',
								gap: 16,
							}}
						>
							<div>
								<Typography.Text style={{ fontSize: '18px', fontWeight: 900 }}>
									{selectedVariant?.title +
										(hasMultipleVariants
											? ` - ${checkOptionsObject(selectedVariant?.optionAttributes)}`
											: '')}
								</Typography.Text>
							</div>
							<div>
								<Typography.Text strong style={{ fontSize: '16px' }}>
									Category:
								</Typography.Text>
								<div
									style={{
										marginTop: 8,
										display: 'flex',
										gap: 8,
										flexWrap: 'wrap',
									}}
								>
									{selectedVariant?.category ? (
										selectedVariant.category
											.split(' / ')
											.map((categorySegment, index) => (
												<Tag
													key={index}
													color={
														[
															'blue',
															'green',
															'orange',
															'purple',
															'cyan',
															'magenta',
														][index % 6]
													}
													style={{ fontSize: '12px' }}
												>
													{categorySegment.trim()}
												</Tag>
											))
									) : (
										<Typography.Text
											type="secondary"
											style={{ fontSize: '14px' }}
										>
											N/A
										</Typography.Text>
									)}
								</div>
							</div>
							<div>
								<Typography.Text
									strong
									style={{
										fontSize: '16px',
										display: 'block',
										marginBottom: 8,
									}}
								>
									Description:
								</Typography.Text>
								<div
									style={{
										fontSize: '14px',
										lineHeight: '1.6',
										maxHeight: '200px',
										overflowY: 'auto',
										border: '1px solid #f0f0f0',
										borderRadius: '6px',
										padding: '12px',
										backgroundColor: '#fafafa',
									}}
									className="rewards-store-description"
									dangerouslySetInnerHTML={{
										__html:
											selectedVariant.information?.description
												?.replace('<p><p>', '')
												?.replace('</p></p>', '') || 'No description available',
									}}
								/>
							</div>
							<div>
								<Typography.Text
									strong
									style={{
										fontSize: '16px',
										display: 'block',
										marginBottom: 8,
									}}
								>
									Product Type:
								</Typography.Text>
								<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
									{selectedVariant.types?.includes('digital') && (
										<Tag color="blue" style={{ fontSize: '12px' }}>
											Digital
										</Tag>
									)}
									{selectedVariant.types?.includes('physical') && (
										<Tag color="magenta" style={{ fontSize: '12px' }}>
											Physical
										</Tag>
									)}
									{selectedVariant.types?.includes('on-demand-swag') && (
										<Tag color="purple" style={{ fontSize: '12px' }}>
											Swag
										</Tag>
									)}
									{(!selectedVariant.types ||
										selectedVariant.types.length === 0) && (
										<Typography.Text type="secondary">
											No type specified
										</Typography.Text>
									)}
								</div>
							</div>
							{hasMultipleVariants && hasOptions && (
								<div>
									<Typography.Text
										strong
										style={{
											fontSize: '16px',
											display: 'block',
											marginBottom: 8,
										}}
									>
										Available Options:
									</Typography.Text>
									<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
										{activeProduct.variants.map((variant, index) => {
											const optionValue = checkOptionsObject(
												variant?.optionAttributes
											);
											const isSelected = selectedVariant.id === variant.id;

											return (
												<Button
													key={variant.id}
													type={isSelected ? 'primary' : 'default'}
													size="small"
													onClick={() => handleVariantChange(variant)}
													style={{
														borderRadius: '16px',
														minWidth: '80px',
													}}
												>
													{optionValue}
												</Button>
											);
										})}
									</div>
								</div>
							)}

							{/* Action Buttons */}
							<div style={{ marginTop: 'auto', paddingTop: 16 }}>
								<div style={{ display: 'flex', gap: 12 }}>
									<Button
										type="primary"
										size="large"
										style={{ flex: 1 }}
										onClick={() => {
											const matchingAvailabilityGroupIds = [];
											if (
												rewardsStoreSettings?.availabilityGroups &&
												selectedCountry
											) {
												rewardsStoreSettings.availabilityGroups.forEach(
													(availabilityGroup) => {
														if (
															availabilityGroup.countryCodes &&
															availabilityGroup.countryCodes.includes(
																selectedCountry
															)
														) {
															matchingAvailabilityGroupIds.push(
																availabilityGroup.id
															);
														}
													}
												);
											}

											const rewardInput = {
												companyId: currentUser.companyId,
												name:
													selectedVariant.title +
													(checkOptionsObject(selectedVariant?.optionAttributes)
														? ' - ' +
															checkOptionsObject(
																selectedVariant?.optionAttributes
															)
														: ''),
												description:
													selectedVariant?.information?.description || '',
												amount:
													selectedBudget.maxBudget *
													(rewardsStoreSettings?.pointsToCurrencyRatio || 100),
												image: selectedVariant?.mediaItems?.[0]?.src || '',
												active: true,
												availabilityGroupIds: matchingAvailabilityGroupIds,
												rewardType: selectedVariant?.category?.includes(
													'Gift Cards'
												)
													? 'giftCards'
													: selectedVariant?.category?.includes('Swag')
														? 'swag'
														: 'gifts',
												externalId: activeProduct.id,
												snappyProduct: {
													id: activeProduct.id,
													brand: activeProduct.brand || null,
													budget: selectedBudget.maxBudget,
													category: activeProduct.category,
													country: selectedCountry,
													coverImage:
														activeProduct?.coverImage?.src ||
														selectedVariant?.mediaItems?.[0]?.src ||
														'',
													description:
														selectedVariant?.information?.description || '',
													mediaItems: activeProduct.mediaItems.map(
														(mediaItem) => mediaItem.src
													),
													options: activeProduct.options || {},
													title: activeProduct.title,
													types: activeProduct.types || [],
													variants: activeProduct.variants.map((variant) => {
														return {
															id: variant.id,
															budget: selectedBudget.maxBudget,
															brand: variant.brand || null,
															category: variant.category,
															country: selectedCountry,
															description: variant?.information?.description,
															optionAttributes: variant.optionAttributes,
															mediaItems: variant.mediaItems.map(
																(mediaItem) => mediaItem.src
															),
															title: variant.title,
															types: variant.types || [],
														};
													}),
												},
											};
											onSubmit(rewardInput);
										}}
									>
										{activeProduct.variants.length > 1
											? 'Add This Variant'
											: 'Add This Reward'}
									</Button>

									{/* Add All Variants Button - Only show when product has multiple variants */}
									{activeProduct?.variants &&
										activeProduct.variants.length > 1 && (
											<Button
												type="default"
												size="large"
												style={{ flex: 1 }}
												onClick={() => {
													const matchingAvailabilityGroupIds = [];
													if (
														rewardsStoreSettings?.availabilityGroups &&
														selectedCountry
													) {
														rewardsStoreSettings.availabilityGroups.forEach(
															(availabilityGroup) => {
																if (
																	availabilityGroup.countryCodes &&
																	availabilityGroup.countryCodes.includes(
																		selectedCountry
																	)
																) {
																	matchingAvailabilityGroupIds.push(
																		availabilityGroup.id
																	);
																}
															}
														);
													}

													activeProduct.variants.forEach((variant) => {
														const rewardInput = {
															companyId: currentUser.companyId,
															name:
																variant.title +
																(checkOptionsObject(variant?.optionAttributes)
																	? ' - ' +
																		checkOptionsObject(
																			variant?.optionAttributes
																		)
																	: ''),
															description:
																variant?.information?.description || '',
															amount:
																selectedBudget.maxBudget *
																(rewardsStoreSettings?.pointsToCurrencyRatio ||
																	100),
															image: variant?.mediaItems?.[0]?.src || '',
															active: true,
															availabilityGroupIds:
																matchingAvailabilityGroupIds,
															rewardType: variant?.category?.includes(
																'Gift Cards'
															)
																? 'giftCards'
																: variant?.category?.includes('Swag')
																	? 'swag'
																	: 'gifts',
															externalId: activeProduct.id,
															snappyProduct: {
																id: activeProduct.id,
																brand: activeProduct.brand || null,
																budget: selectedBudget.maxBudget,
																category: activeProduct.category,
																country: selectedCountry,
																coverImage:
																	activeProduct?.coverImage?.src ||
																	variant?.mediaItems?.[0]?.src ||
																	'',
																description:
																	variant?.information?.description || '',
																mediaItems: activeProduct.mediaItems.map(
																	(mediaItem) => mediaItem.src
																),
																options: activeProduct.options || {},
																title: activeProduct.title,
																types: activeProduct.types || [],
																variants: activeProduct.variants.map((v) => {
																	return {
																		id: v.id,
																		budget: selectedBudget.maxBudget,
																		brand: v.brand || null,
																		category: v.category,
																		country: selectedCountry,
																		description: v?.information?.description,
																		optionAttributes: v.optionAttributes,
																		mediaItems: v.mediaItems.map(
																			(mediaItem) => mediaItem.src
																		),
																		title: v.title,
																		types: v.types || [],
																	};
																}),
															},
														};
														onSubmit(rewardInput);
													});
												}}
											>
												Add All Variants
											</Button>
										)}
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</div>
		);
	};

	const renderSnappyWizard = () => (
		<div style={{ padding: 0 }}>
			<Row
				gutter={16}
				style={{ marginTop: 6, marginBottom: 0, maxHeight: '42px' }}
			>
				<Col span={7}>
					<Form.Item label="Country">
						<Select
							placeholder="Select country"
							value={selectedCountry}
							onChange={setSelectedCountry}
							options={countryOptions}
							style={{ width: '100%', height: '40px !important' }}
						/>
					</Form.Item>
				</Col>
				<Col span={5}>
					<Form.Item label="Budget">
						<Select
							placeholder="Select budget range"
							value={selectedBudgetValue}
							onChange={handleBudgetChange}
							options={budgetRanges.map((range) => ({
								label: range.label,
								value: range.value,
							}))}
							style={{ width: '100%' }}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item label="Search Products">
						<Input.Search
							className="rewards-store-search"
							placeholder="Search for products..."
							value={currentSearchTerm}
							onChange={(e) => setCurrentSearchTerm(e.target.value)}
							onSearch={handleSearch}
							enterButton
							style={{ width: '100%' }}
						/>
					</Form.Item>
				</Col>
			</Row>

			<div style={{ marginTop: 0 }}>
				{renderBreadcrumb()}

				<div
					style={{
						height: '655px',
						overflowY: 'auto',
						border: '1px solid #f0f0f0',
						borderRadius: '8px',
						padding: '16px',
					}}
				>
					{!selectedCountry || !selectedBudgetValue ? (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100%',
								color: '#595959',
								textAlign: 'center',
							}}
						>
							Please choose at least one country and a budget to browse
							collections
						</div>
					) : collectionsLoading ? (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100%',
							}}
						>
							<Spin size="large" />
						</div>
					) : wizardStep === 'collections' && collections.length > 0 ? (
						<Row gutter={[16, 16]}>
							{collections.map((result) => (
								<Col xs={24} sm={12} md={8} key={result.id}>
									<AnimatedRewardCard
										item={result}
										onClick={handleCollectionClick}
										type="collection"
									/>
								</Col>
							))}
						</Row>
					) : wizardStep === 'products' ? (
						<>
							{productsLoading ? (
								<div
									style={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										height: 'calc(100% - 60px)',
									}}
								>
									<Spin size="large" />
								</div>
							) : productsByCollection[selectedCollection?.id]?.length > 0 ? (
								<Row gutter={[16, 16]}>
									{productsByCollection[selectedCollection.id].map(
										(product) => (
											<Col xs={24} sm={12} md={8} key={product.id}>
												<AnimatedRewardCard
													item={product}
													onClick={handleProductClick}
													type="product"
												/>
											</Col>
										)
									)}
								</Row>
							) : (
								<div
									style={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										height: 'calc(100% - 60px)',
										color: '#595959',
									}}
								>
									No Products Found
								</div>
							)}
						</>
					) : wizardStep === 'details' ? (
						<div>{renderProductDetails()}</div>
					) : wizardStep === 'collections' ? (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								height: '100%',
								color: '#595959',
							}}
						>
							No Collections Found
						</div>
					) : null}
				</div>
			</div>
		</div>
	);

	const tabItems = [
		{
			key: 'snappy',
			label: 'Select from Snappy',
			children: renderSnappyWizard(),
		},
		{
			key: 'manual',
			label: 'Create Manually',
			children: renderManualForm(),
		},
	];

	return (
		<Modal
			title="Add New Reward"
			open={open}
			centered
			onCancel={handleCancel}
			onOk={activeTab === 'manual' ? () => form.submit() : undefined}
			width={1000}
			confirmLoading={loading}
			footer={activeTab === 'snappy' ? null : undefined}
		>
			<Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
		</Modal>
	);
};

export default AddRewardModal;
